import PaginationArrow from "@/assets/icons/PaginationArrow";
import HorizontalPaginationButton from "@/ui/components/HorizontalPaginationButton";
import { useIsMobile } from "@/ui";
import { accentDividerStyle } from "@canucci/base-mantine/theme";
import { Box, Group, Stack, Text, useMantineTheme } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import styles from "./Carousel.module.css";

export type CarouselItem = {
  image?: string;
  title: string;
  description: string;
};

export type CarouselProps = {
  items: CarouselItem[];
  backgroundImage?: string;
  backgroundColor?: string;
  backgroundStyle?: CSSProperties;
  currentIndex: number;
  onCurrentIndexChange: (index: number) => void;
  showTitlePre?: boolean;
};

function Carousel({
  items,
  backgroundImage,
  backgroundColor,
  backgroundStyle,
  currentIndex,
  onCurrentIndexChange,
  showTitlePre = true,
}: CarouselProps) {
  const { t } = useTranslation("pages");
  const theme = useMantineTheme();
  const { spacing, semanticColors } = theme.other;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [maxContentHeight, setMaxContentHeight] = useState<number | null>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isInitialMount = useRef(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Adjust the size of the refs array when items.length changes.
    // Existing refs are preserved, new slots are null.
    contentRefs.current = contentRefs.current.slice(0, items.length);
  }, [items.length]);

  useLayoutEffect(() => {
    const calculateHeight = () => {
      if (items.length === 0) {
        setMaxContentHeight(null);
        return;
      }

      let maxHeight = 0;
      contentRefs.current.forEach((ref) => {
        if (ref) {
          // scrollHeight includes the element's padding (Stack's py={4})
          maxHeight = Math.max(maxHeight, ref.scrollHeight);
        }
      });

      if (maxHeight > 0) {
        setMaxContentHeight(maxHeight);
      } else {
        // Fallback if all measured heights are 0
        setMaxContentHeight(null);
      }
    };

    calculateHeight(); // Initial calculation

    window.addEventListener("resize", calculateHeight);
    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  }, [items]); // Recalculate when items array reference changes

  useEffect(() => {
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.offsetWidth;
      const scrollBehavior = isInitialMount.current && currentIndex > 0 ? "auto" : "smooth";
      scrollContainerRef.current.scrollTo({
        left: currentIndex * containerWidth,
        behavior: scrollBehavior,
      });

      if (isInitialMount.current) {
        isInitialMount.current = false;
      }
    }
  }, [currentIndex, items]);

  useEffect(() => {
    const handleResize = () => {
      if (scrollContainerRef.current) {
        const containerWidth = scrollContainerRef.current.offsetWidth;
        // Instantly adjust scroll position without smooth animation
        scrollContainerRef.current.scrollLeft = currentIndex * containerWidth;
      }
    };

    window.addEventListener("resize", handleResize);

    // Clean up the event listener when the component unmounts or dependencies change
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [currentIndex, items]); // Re-run this effect if currentIndex or items change

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      const target = event.target;
      if (target instanceof HTMLElement) {
        const isEditable =
          target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
          !!target.closest("[role='dialog']");
        if (isEditable) {
          return;
        }
      }

      if (event.key === "ArrowLeft" && currentIndex > 0) {
        event.preventDefault();
        onCurrentIndexChange(currentIndex - 1);
      } else if (event.key === "ArrowRight" && currentIndex < items.length - 1) {
        event.preventDefault();
        onCurrentIndexChange(currentIndex + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, items.length, onCurrentIndexChange]);

  if (items.length === 0) {
    return null;
  }

  function handlePrev() {
    if (currentIndex > 0) {
      onCurrentIndexChange(currentIndex - 1);
    }
  }

  function handleNext() {
    if (currentIndex < items.length - 1) {
      onCurrentIndexChange(currentIndex + 1);
    }
  }

  return (
    <Box w="100%" pos="relative" h={maxContentHeight ? `${maxContentHeight}px` : { base: "10rem", md: "16rem" }}>
      {/* Background layer aligned with content below */}
      <Box
        pos="absolute"
        top={0}
        bottom={0}
        left={isMobile ? 0 : spacing.sectionGap}
        right={isMobile ? 0 : spacing.sectionGap}
        className={styles.background}
        style={{
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
          backgroundColor: backgroundColor,
          ...backgroundStyle,
        }}
      />

      {/* Narrower carousel container with overflow hidden for clipping */}
      <Box
        pos="absolute"
        top={0}
        bottom={0}
        left={isMobile ? 0 : { base: theme.spacing.md, md: theme.spacing["4xl"] }}
        right={isMobile ? 0 : { base: theme.spacing.md, md: theme.spacing["4xl"] }}
        className={styles.carouselWrapper}
      >
        <Box ref={scrollContainerRef} h="100%" w="100%" className={styles.scrollContainer} pos="relative">
          <Box
            className={styles.carouselContainer}
            w={`${items.length * 100}%`}
            pos="absolute"
            top={0}
            left={0}
            h="100%"
          >
            {items.map((item, index) => {
              return (
                <Box
                  key={index}
                  w={`${100 / items.length}%`}
                  h="100%"
                  className={styles.carouselItem}
                  style={{
                    // Dynamic background image per item
                    backgroundImage: item.image ? `url(${item.image})` : undefined,
                  }}
                >
                  <Stack
                    ref={(el) => {
                      contentRefs.current[index] = el;
                    }}
                    w="100%"
                    py={{
                      base: theme.spacing["3xl"],
                      md: theme.spacing["4xl"],
                    }}
                    px={theme.spacing["3xl"]}
                    justify="center"
                    align="center"
                  >
                    <Stack className={styles.carouselContentStack}>
                      {showTitlePre && (
                        <Text className={styles.carouselPre}>{t("canvas.carousel.carouselTitlePre")}</Text>
                      )}
                      <Text className={styles.carouselTitle}>{item.title}</Text>
                      <Box style={accentDividerStyle} mx="auto" mt="sm" />
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Navigation buttons in separate full-width container */}
      <Box w="100%" pos="absolute" top={0} left={0} bottom={0} right={0} className={styles.navigationContainer}>
        {currentIndex > 0 ? (
          <HorizontalPaginationButton
            onClick={handlePrev}
            disabled={false}
            style={{
              left: isMobile ? theme.spacing.xs : theme.spacing.xl,
              zIndex: 100,
            }}
          >
            <PaginationArrow direction="left" color={semanticColors.invertedText} />
          </HorizontalPaginationButton>
        ) : null}
        {currentIndex < items.length - 1 ? (
          <HorizontalPaginationButton
            onClick={handleNext}
            disabled={false}
            style={{
              right: isMobile ? theme.spacing.xs : theme.spacing.xl,
              zIndex: 100,
            }}
          >
            <PaginationArrow direction="right" color={semanticColors.invertedText} />
          </HorizontalPaginationButton>
        ) : null}
      </Box>

      {/* Pagination dots */}
      <Group
        w="100%"
        pos="absolute"
        left={0}
        bottom={theme.spacing.lg}
        right={0}
        className={styles.paginationContainer}
        gap={theme.spacing.xs}
        justify="center"
      >
        {items.map((_, index) => {
          return (
            <Box
              component="button"
              key={index}
              className={styles.paginationDot}
              style={{
                // 🔒 EXCEPTION: Dynamic opacity based on current index
                opacity: index === currentIndex ? 1 : 0.33,
                // 🔒 DESIGN-CRITICAL: Exact size for pagination dots
                width: spacing.paginationDotSize,
                height: spacing.paginationDotSize,
              }}
              onClick={() => onCurrentIndexChange(index)}
            />
          );
        })}
      </Group>
    </Box>
  );
}

export default Carousel;
